<template>
    <div class="blog-detalle-page">
        <!-- Header -->
        <section class="page-header">
            <div class="container">
                <nav class="breadcrumbs">
                    <router-link to="/">Inicio</router-link>
                    <span class="separator">/</span>
                    <router-link to="/blog">Blog</router-link>
                    <span class="separator">/</span>
                    <span class="current">{{ blog && blog.titulo || '...' }}</span>
                </nav>
            </div>
        </section>
        
        <div class="container" v-if="!loading && blog">
            <article class="blog-article">
                <!-- Featured Image -->
                <div class="article-image" v-if="blog.imagen">
                    <img :src="`/storage/${blog.imagen}`" :alt="blog.titulo">
                </div>
                
                <!-- Article Header -->
                <header class="article-header">
                    <div class="article-meta">
                        <span class="meta-item date">
                            <i class="fas fa-calendar"></i>
                            {{ formatDate(blog.created_at) }}
                        </span>
                        <span class="meta-item date" v-if="blog.updated_at && blog.updated_at !== blog.created_at">
                            <i class="fas fa-sync"></i>
                            Actualizado: {{ formatDate(blog.updated_at) }}
                        </span>
                    </div>
                    
                    <h1 class="article-title">{{ blog.titulo }}</h1>
                    
                    <div class="article-excerpt" v-if="blog.descripcion">
                        <p>{{ blog.descripcion }}</p>
                    </div>
                </header>
                
                <!-- Article Content -->
                <div class="article-content">
                    <div v-html="blog.descripcion"></div>
                </div>
                
                <!-- Share Buttons -->
                <div class="article-share">
                    <h4>Compartir este artículo</h4>
                    <div class="share-buttons">
                        <a :href="shareUrls.facebook" target="_blank" class="share-btn facebook">
                            <i class="fab fa-facebook-f"></i>
                        </a>
                        <a :href="shareUrls.twitter" target="_blank" class="share-btn twitter">
                            <i class="fab fa-twitter"></i>
                        </a>
                        <a :href="shareUrls.linkedin" target="_blank" class="share-btn linkedin">
                            <i class="fab fa-linkedin-in"></i>
                        </a>
                        <a :href="shareUrls.whatsapp" target="_blank" class="share-btn whatsapp">
                            <i class="fab fa-whatsapp"></i>
                        </a>
                    </div>
                </div>
                
                <!-- Back to Blog Button -->
                <div class="back-button-container">
                    <router-link to="/blog" class="back-btn">
                        <i class="fas fa-arrow-left"></i>
                        Volver al Blog
                    </router-link>
                </div>
            </article>
            
            <!-- Related Posts -->
            <section class="related-posts" v-if="relatedPosts.length">
                <h2 class="section-title">Artículos Relacionados</h2>
                <div class="blog-grid">
                    <article 
                        v-for="post in relatedPosts" 
                        :key="post.id"
                        class="blog-card"
                        @click="goToBlog(post)"
                    >
                        <div class="blog-image">
                            <img :src="getBlogImage(post)" :alt="post.titulo">
                            <div class="blog-overlay">
                                <i class="fas fa-arrow-right"></i>
                            </div>
                        </div>
                        
                        <div class="blog-content">
                            <div class="blog-meta">
                                <span class="meta-badge date">
                                    <i class="fas fa-calendar"></i>
                                    {{ formatDate(post.created_at) }}
                                </span>
                            </div>
                            
                            <h3 class="blog-title">{{ post.titulo }}</h3>
                            
                            <p v-if="post.descripcion" class="blog-excerpt">
                                {{ post.descripcion }}
                            </p>
                        </div>
                    </article>
                </div>
            </section>
        </div>
        
        <div class="container" v-else-if="loading">
            <div class="loading-state">
                <div class="spinner"></div>
                <p>Cargando artículo...</p>
            </div>
        </div>
    </div>
</template>

<script>
import api from '../services/api';

export default {
    name: 'BlogDetalle',
    props: {
        slug: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            blog: null,
            relatedPosts: [],
            loading: true
        };
    },
    computed: {
        shareUrls() {
            const url = encodeURIComponent(window.location.href);
            const title = encodeURIComponent(this.blog && this.blog.titulo || '');
            
            return {
                facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
                twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
                linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
                whatsapp: `https://wa.me/?text=${title}%20${url}`
            };
        }
    },
    async created() {
        await this.loadBlog();
    },
    watch: {
        slug: 'loadBlog'
    },
    methods: {
        async loadBlog() {
            try {
                this.loading = true;
                
                const { data } = await api.getBlogsBySlug(this.slug);
                this.blog = data;
                console.log('Blog cargado:', this.blog);
                
                // Load related posts (últimos 3 blogs excluyendo el actual)
                const blogsRes = await api.getBlogs();
                const allBlogs = blogsRes.data.data || blogsRes.data || [];
                this.relatedPosts = allBlogs
                    .filter(b => b.id !== this.blog.id)
                    .slice(0, 3);
            } catch (error) {
                console.error('Error loading blog:', error);
            } finally {
                this.loading = false;
            }
        },
        formatDate(date) {
            if (!date) return '';
            const options = { year: 'numeric', month: 'long', day: 'numeric' };
            return new Date(date).toLocaleDateString('es-ES', options);
        },
        getBlogImage(blog) {
            if (blog.imagen) {
                return `/storage/${blog.imagen}`;
            }
            return 'https://via.placeholder.com/600x400/1e3a5f/d4af37?text=Blog';
        },
        goToBlog(blog) {
            this.$router.push({ 
                name: 'blog.detalle', 
                params: { slug: blog.slug }
            });
        }
    }
};
</script>

<style scoped>
/* VARIABLES */
:root {
    --primary-dark: #0a0e27;
    --secondary-dark: #1a1f3a;
    --accent-gold: #d4af37;
    --accent-teal: #2dd4bf;
    --text-light: #e2e8f0;
    --text-gray: #94a3b8;
    --card-bg: rgba(26, 31, 58, 0.6);
}

.blog-detalle-page {
    min-height: 100vh;
    background: linear-gradient(135deg, var(--primary-dark) 0%, var(--secondary-dark) 100%);
    color: var(--text-light);
}

/* PAGE HEADER */
.page-header {
    background: linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(45, 212, 191, 0.05) 100%);
    border-bottom: 1px solid rgba(212, 175, 55, 0.2);
    padding: 2rem 0;
    margin-bottom: 3rem;
}

.breadcrumbs {
    font-size: 0.9rem;
    color: var(--text-gray);
}

.breadcrumbs a {
    color: var(--accent-gold);
    text-decoration: none;
    transition: color 0.3s ease;
}

.breadcrumbs a:hover {
    color: var(--accent-teal);
}

.separator {
    margin: 0 0.5rem;
}

.current {
    color: var(--text-light);
}

/* CONTAINER */
.container {
    max-width: 900px;
    margin: 0 auto;
    padding: 0 2rem;
}

/* ARTICLE */
.blog-article {
    background: var(--card-bg);
    border-radius: 20px;
    overflow: hidden;
    border: 1px solid rgba(212, 175, 55, 0.2);
    margin-bottom: 5rem;
    backdrop-filter: blur(10px);
}

.article-image {
    width: 100%;
    max-height: 500px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.05);
}

.article-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.article-header {
    padding: 3rem 3rem 2rem;
}

.article-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.meta-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.875rem;
    font-weight: 600;
}

.meta-item.date {
    background: rgba(212, 175, 55, 0.1);
    color: var(--accent-gold);
    border: 1px solid rgba(212, 175, 55, 0.3);
}

.meta-item.author {
    background: rgba(45, 212, 191, 0.1);
    color: var(--accent-teal);
    border: 1px solid rgba(45, 212, 191, 0.3);
}

.meta-item.category {
    background: rgba(139, 92, 246, 0.1);
    color: #a78bfa;
    border: 1px solid rgba(139, 92, 246, 0.3);
}

.meta-item i {
    font-size: 0.875rem;
}

.article-title {
    font-family: 'Playfair Display', serif;
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--text-light);
    line-height: 1.2;
    margin-bottom: 1.5rem;
}

.article-excerpt {
    padding: 1.5rem;
    background: rgba(212, 175, 55, 0.05);
    border-left: 4px solid var(--accent-gold);
    border-radius: 10px;
}

.article-excerpt p {
    font-size: 1.125rem;
    color: var(--text-gray);
    line-height: 1.6;
    margin: 0;
}

.article-content {
    padding: 2rem 3rem 3rem;
    color: var(--text-light);
    font-size: 1.125rem;
    line-height: 1.8;
}

.article-content >>> h2,
.article-content >>> h3,
.article-content >>> h4 {
    font-weight: 700;
    color: var(--text-light);
    margin-top: 2rem;
    margin-bottom: 1rem;
}

.article-content >>> h2 {
    font-size: 2rem;
    color: var(--accent-gold);
}

.article-content >>> h3 {
    font-size: 1.5rem;
    color: var(--accent-teal);
}

.article-content >>> h4 {
    font-size: 1.25rem;
}

.article-content >>> p {
    margin-bottom: 1.5rem;
}

.article-content >>> ul,
.article-content >>> ol {
    margin-bottom: 1.5rem;
    padding-left: 2rem;
}

.article-content >>> li {
    margin-bottom: 0.5rem;
}

.article-content >>> img {
    max-width: 100%;
    height: auto;
    border-radius: 10px;
    margin: 2rem 0;
    border: 1px solid rgba(212, 175, 55, 0.2);
}

.article-content >>> blockquote {
    border-left: 4px solid var(--accent-gold);
    padding-left: 1.5rem;
    margin: 2rem 0;
    font-style: italic;
    color: var(--text-gray);
}

.article-content >>> code {
    background: rgba(255, 255, 255, 0.1);
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    font-size: 0.9em;
    color: var(--accent-teal);
}

.article-content >>> pre {
    background: rgba(10, 14, 39, 0.8);
    color: var(--text-light);
    padding: 1.5rem;
    border-radius: 10px;
    overflow-x: auto;
    margin: 2rem 0;
    border: 1px solid rgba(212, 175, 55, 0.2);
}

.article-content >>> pre code {
    background: transparent;
    padding: 0;
}

.article-content >>> a {
    color: var(--accent-gold);
    text-decoration: underline;
}

.article-content >>> a:hover {
    color: var(--accent-teal);
}

.article-share {
    padding: 2rem 3rem 2rem;
    border-top: 1px solid rgba(212, 175, 55, 0.2);
}

.article-share h4 {
    font-size: 1.125rem;
    font-weight: 700;
    color: var(--text-light);
    margin-bottom: 1rem;
}

.share-buttons {
    display: flex;
    gap: 1rem;
}

.share-btn {
    width: 45px;
    height: 45px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    text-decoration: none;
    transition: all 0.3s ease;
}

.share-btn:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
}

.share-btn.facebook {
    background: #1877f2;
}

.share-btn.twitter {
    background: #1da1f2;
}

.share-btn.linkedin {
    background: #0077b5;
}

.share-btn.whatsapp {
    background: #25d366;
}

.back-button-container {
    padding: 0 3rem 3rem;
}

.back-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 2rem;
    background: rgba(212, 175, 55, 0.1);
    border: 2px solid var(--accent-gold);
    border-radius: 50px;
    color: var(--accent-gold);
    text-decoration: none;
    font-weight: 600;
    transition: all 0.3s ease;
}

.back-btn:hover {
    background: var(--accent-gold);
    color: var(--primary-dark);
    transform: translateX(-5px);
}

/* RELATED POSTS */
.related-posts {
    margin-bottom: 5rem;
}

.section-title {
    font-family: 'Playfair Display', serif;
    font-size: 2rem;
    font-weight: 700;
    color: var(--text-light);
    margin-bottom: 2rem;
    background: linear-gradient(135deg, var(--accent-gold), var(--accent-teal));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.blog-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 2rem;
}

.blog-card {
    background: var(--card-bg);
    border-radius: 20px;
    overflow: hidden;
    border: 1px solid rgba(212, 175, 55, 0.1);
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
}

.blog-card:hover {
    transform: translateY(-10px);
    border-color: var(--accent-gold);
    box-shadow: 0 10px 30px rgba(212, 175, 55, 0.2);
}

.blog-image {
    position: relative;
    width: 100%;
    height: 200px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.05);
}

.blog-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
}

.blog-card:hover .blog-image img {
    transform: scale(1.1);
}

.blog-overlay {
    position: absolute;
    inset: 0;
    background: rgba(10, 14, 39, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.blog-card:hover .blog-overlay {
    opacity: 1;
}

.blog-overlay i {
    font-size: 2rem;
    color: var(--accent-gold);
}

.blog-content {
    padding: 1.5rem;
}

.blog-meta {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1rem;
}

.meta-badge {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
}

.meta-badge.date {
    background: rgba(212, 175, 55, 0.1);
    color: var(--accent-gold);
    border: 1px solid rgba(212, 175, 55, 0.3);
}

.blog-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.25rem;
    color: var(--text-light);
    margin-bottom: 1rem;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.blog-excerpt {
    color: var(--text-gray);
    font-size: 0.9rem;
    line-height: 1.6;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

/* LOADING STATE */
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 5rem 2rem;
    text-align: center;
}

.spinner {
    width: 50px;
    height: 50px;
    border: 4px solid rgba(212, 175, 55, 0.1);
    border-top-color: var(--accent-gold);
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.loading-state p {
    margin-top: 1rem;
    color: var(--text-gray);
}

/* RESPONSIVE */
@media (max-width: 768px) {
    .container {
        padding: 0 1rem;
    }
    
    .article-header,
    .article-content,
    .article-share,
    .back-button-container {
        padding-left: 1.5rem;
        padding-right: 1.5rem;
    }
    
    .article-title {
        font-size: 2rem;
    }
    
    .article-content {
        font-size: 1rem;
    }
    
    .blog-grid {
        grid-template-columns: 1fr;
    }
}
</style>